import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { executeAthenaQuery, getAthenaQueryStatus, getAthenaQueryResults } from '../services/api';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const TierCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 140px;
`;

const TierHeader = styled.div`
  font-size: 11px;
  opacity: 0.9;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TierValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const PointsValue = styled.div`
  font-size: 12px;
  opacity: 0.9;
`;

const LoadingCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px;
  color: #666;
  font-size: 13px;
  font-style: italic;
  min-width: 140px;
`;

const ErrorCard = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 12px 16px;
  color: #721c24;
  font-size: 13px;
  min-width: 140px;
`;

interface QuarterData {
  tier: string;
  points: number;
}

interface TierHistoryData {
  q1: QuarterData | null;
  q2: QuarterData | null;
  q3: QuarterData | null;
  q4: QuarterData | null;
}

interface TierHistoryProps {
  userData: any;
  isSuperAdmin: boolean;
}

const TierHistory: React.FC<TierHistoryProps> = ({ userData, isSuperAdmin }) => {
  const [tierData, setTierData] = useState<TierHistoryData | null>(null);
  const [loadingStates, setLoadingStates] = useState({ q1: false, q2: false, q3: false, q4: false });
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('TierHistory useEffect triggered');
    console.log('isSuperAdmin:', isSuperAdmin);
    console.log('userData:', userData);
    console.log('userData?.external_ids:', userData?.external_ids);
    console.log('userData?.external_ids?.mariana_tek:', userData?.external_ids?.mariana_tek);
    console.log('userData?.user?.external_id:', userData?.user?.external_id);
    
    if (isSuperAdmin && userData?.user?.external_id) {
      console.log('Calling fetchAllTierHistory with external_id:', userData.user.external_id);
      fetchAllTierHistory(userData.user.external_id);
    } else if (isSuperAdmin && userData?.external_ids?.mariana_tek) {
      console.log('Calling fetchAllTierHistory with mariana_tek:', userData.external_ids.mariana_tek);
      fetchAllTierHistory(userData.external_ids.mariana_tek);
    } else {
      console.log('TierHistory not triggered - conditions not met');
    }
  }, [userData, isSuperAdmin]);

  const fetchAllTierHistory = async (marianaTekId: string) => {
    setError('');
    setTierData({ q1: null, q2: null, q3: null, q4: null });
    setLoadingStates({ q1: true, q2: true, q3: true, q4: true });

    // Fetch all quarters in parallel
    const promises = [
      fetchQuarterData(marianaTekId, 'q1', 'q1_2025'),
      fetchQuarterData(marianaTekId, 'q2', 'q2_2025'),
      fetchQuarterData(marianaTekId, 'q3', 'q3_2025'),
      fetchQuarterData(marianaTekId, 'q4', 'q4_2025')
    ];

    try {
      await Promise.all(promises);
    } catch (err: any) {
      console.error('Error fetching tier history:', err);
      setError('Failed to load some tier history data');
    }
  };

  const fetchQuarterData = async (marianaTekId: string, quarter: 'q1' | 'q2' | 'q3' | 'q4', tableName: string) => {
    try {
      const query = `
        SELECT 
          ${tableName}.tier as "Tier Level", 
          ${tableName}.points as "Points Total"
        FROM ${tableName}
        WHERE external_id = '${marianaTekId}'
      `;

      console.log(`Executing ${quarter.toUpperCase()} Athena query for MarianaTek ID:`, marianaTekId);
      console.log('Query:', query);

      // Execute the query
      const executeResponse = await executeAthenaQuery(
        query,
        'historical_tier_data',
        's3://barrys-s3/Historical Tier Data/'
      );

      console.log(`${quarter.toUpperCase()} query execution response:`, executeResponse);

      const queryExecutionId = executeResponse.queryExecutionId;

      // Poll for completion
      await pollQueryCompletion(queryExecutionId, quarter);

    } catch (err: any) {
      console.error(`${quarter.toUpperCase()} tier history fetch error:`, err);
      setLoadingStates(prev => ({ ...prev, [quarter]: false }));
    }
  };

  const pollQueryCompletion = async (queryExecutionId: string, quarter: 'q1' | 'q2' | 'q3' | 'q4'): Promise<void> => {
    try {
      const statusResponse = await getAthenaQueryStatus(queryExecutionId);
      console.log(`${quarter.toUpperCase()} query status response:`, statusResponse);
      
      if (statusResponse.status === 'SUCCEEDED') {
        // Query completed successfully, fetch results
        const resultsResponse = await getAthenaQueryResults(queryExecutionId);
        console.log(`${quarter.toUpperCase()} query results response:`, resultsResponse);
        
        if (resultsResponse.data && resultsResponse.data.length > 0) {
          const data = resultsResponse.data[0];
          const quarterData: QuarterData = {
            tier: data['Tier Level'] || 'N/A',
            points: data['Points Total'] || 0
          };
          
          setTierData(prev => prev ? { ...prev, [quarter]: quarterData } : { q1: null, q2: null, q3: null, q4: null, [quarter]: quarterData });
        } else {
          console.log(`No ${quarter.toUpperCase()} data found in query results`);
        }
        setLoadingStates(prev => ({ ...prev, [quarter]: false }));
        
      } else if (statusResponse.status === 'FAILED' || statusResponse.status === 'CANCELLED') {
        console.error(`${quarter.toUpperCase()} query failed:`, statusResponse.statusChangeReason);
        setLoadingStates(prev => ({ ...prev, [quarter]: false }));
        
      } else if (statusResponse.status === 'RUNNING' || statusResponse.status === 'QUEUED') {
        // Continue polling
        setTimeout(() => pollQueryCompletion(queryExecutionId, quarter), 2000);
        
      } else {
        console.error(`Unknown ${quarter.toUpperCase()} query status:`, statusResponse.status);
        setLoadingStates(prev => ({ ...prev, [quarter]: false }));
      }
    } catch (err: any) {
      console.error(`${quarter.toUpperCase()} query polling error:`, err);
      setLoadingStates(prev => ({ ...prev, [quarter]: false }));
    }
  };

  // Don't render anything if not super admin
  if (!isSuperAdmin) {
    console.log('TierHistory: Not rendering - not super admin');
    return null;
  }

  console.log('TierHistory: Rendering component', { loadingStates, error, tierData });

  const isLoading = loadingStates.q1 || loadingStates.q2 || loadingStates.q3 || loadingStates.q4;
  const hasAnyData = tierData && (tierData.q1 || tierData.q2 || tierData.q3 || tierData.q4);

  return (
    <Container>
      {error && (
        <ErrorCard>{error}</ErrorCard>
      )}
      
      {/* Q1 Card */}
      {loadingStates.q1 ? (
        <LoadingCard>Loading Q1 data...</LoadingCard>
      ) : tierData?.q1 ? (
        <TierCard>
          <TierHeader>Q1 2025</TierHeader>
          <TierValue>Tier: {tierData.q1.tier}</TierValue>
          <PointsValue>Points: {tierData.q1.points?.toLocaleString()}</PointsValue>
        </TierCard>
      ) : (
        <LoadingCard>No Q1 data available</LoadingCard>
      )}
      
      {/* Q2 Card */}
      {loadingStates.q2 ? (
        <LoadingCard>Loading Q2 data...</LoadingCard>
      ) : tierData?.q2 ? (
        <TierCard>
          <TierHeader>Q2 2025</TierHeader>
          <TierValue>Tier: {tierData.q2.tier}</TierValue>
          <PointsValue>Points: {tierData.q2.points?.toLocaleString()}</PointsValue>
        </TierCard>
      ) : (
        <LoadingCard>No Q2 data available</LoadingCard>
      )}
      
      {/* Q3 Card */}
      {loadingStates.q3 ? (
        <LoadingCard>Loading Q3 data...</LoadingCard>
      ) : tierData?.q3 ? (
        <TierCard>
          <TierHeader>Q3 2025</TierHeader>
          <TierValue>Tier: {tierData.q3.tier}</TierValue>
          <PointsValue>Points: {tierData.q3.points?.toLocaleString()}</PointsValue>
        </TierCard>
      ) : (
        <LoadingCard>No Q3 data available</LoadingCard>
      )}

      {/* Q4 Card */}
      {loadingStates.q4 ? (
        <LoadingCard>Loading Q4 data...</LoadingCard>
      ) : tierData?.q4 ? (
        <TierCard>
          <TierHeader>Q4 2025</TierHeader>
          <TierValue>Tier: {tierData.q4.tier}</TierValue>
          <PointsValue>Points: {tierData.q4.points?.toLocaleString()}</PointsValue>
        </TierCard>
      ) : (
        <LoadingCard>No Q4 data available</LoadingCard>
      )}

      {!isLoading && !hasAnyData && !error && (
        <LoadingCard>No historical tier data available</LoadingCard>
      )}
    </Container>
  );
};

export default TierHistory;